import { normalizeCourseId } from "./normalize-course-id";
import type { CalendarEvent } from "../types";

/**
 * The set of course codes a single calendar event serves: its primary
 * `courseId` plus every code in the comma-delimited `additionalCourseIds`
 * (a multi-value `Course ID: A, B, C` line = one physical section that serves
 * codes A + B + C). Mirrors the backend's `AcceptedNormalizedCourseIds`.
 *
 * Returned in **normalized** form (trim + upper) and de-duplicated, so callers
 * can test membership directly against a normalized target code. Empty codes
 * are dropped. Extracted so the event-detail roster and the calendar
 * course-highlight share one derivation instead of two copies of the split.
 */
export function acceptedNormalizedCourseIds(
  event: Pick<CalendarEvent, "courseId" | "additionalCourseIds">,
): string[] {
  const codes = new Set<string>();
  const primary = normalizeCourseId(event.courseId);
  if (primary) codes.add(primary);
  for (const raw of event.additionalCourseIds?.split(",") ?? []) {
    const n = normalizeCourseId(raw);
    if (n) codes.add(n);
  }
  return [...codes];
}

/**
 * True when `event` serves `courseId` (matching on the primary code or any
 * additional code). `courseId` is normalized here, so callers may pass the raw
 * code straight off an issue/request. Returns false for a blank target.
 */
export function eventServesCourse(
  event: Pick<CalendarEvent, "courseId" | "additionalCourseIds">,
  courseId: string | null | undefined,
): boolean {
  const target = normalizeCourseId(courseId);
  if (!target) return false;
  return acceptedNormalizedCourseIds(event).includes(target);
}
